import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { useMe, useLogout } from "@/lib/auth";
import i18n, { t, supportedLanguages } from "@/i18n";
import { Select } from "@/components/ui";

/**
 * Protected `/app` layout. Mirrors LinguaQuest's app.tsx: a `beforeLoad`
 * guard redirects to `/` when there's no session (the server still enforces
 * auth on every /api call), then renders a header + <Outlet>.
 */
export const Route = createFileRoute("/app")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (res.status === 401) throw redirect({ to: "/" });
  },
  component: AppLayout,
});

const NAV = [
  { to: "/app/dashboard", label: "nav.dashboard" },
  { to: "/app/assignments", label: "nav.repository" },
  { to: "/app/classes", label: "nav.classes" },
  { to: "/app/links", label: "nav.links" },
  { to: "/app/assignment-types", label: "nav.types" },
] as const;

function AppLayout() {
  const { data: me } = useMe();
  const logout = useLogout();

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-6 py-3">
          <span className="font-semibold">{t("app.name")}</span>
          <nav className="flex items-center gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-3 py-1.5 text-sm hover:bg-muted"
                activeProps={{ className: "bg-muted font-medium" }}
              >
                {t(item.label)}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <Select
              value={i18n.language?.slice(0, 2) || "en"}
              onChange={(e) => {
                void i18n.changeLanguage(e.target.value);
                localStorage.setItem("ui-lang", e.target.value);
              }}
              aria-label={t("nav.language")}
            >
              {supportedLanguages.map((l) => (
                <option key={l} value={l}>
                  {l === "es" ? "Español" : "English"}
                </option>
              ))}
            </Select>
            {me && (
              <span className="hidden text-sm text-muted-foreground sm:inline">
                {me.name} · {me.email}
              </span>
            )}
            <button
              className="rounded-md border px-3 py-1 text-sm hover:bg-muted"
              onClick={() => {
                logout.mutate();
                window.location.href = "/";
              }}
            >
              {t("nav.signOut")}
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

